import React, { useState } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Paper, Typography, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';

export interface DelayReason {
  reason: string;
  count: number;
  avgMinutes?: number | null;
}

interface DelaysBreakdownProps {
  reasons: DelayReason[];
  /** Fixed chart height in px. When set, skips aspect-ratio sizing so wide panels stay short. */
  height?: number;
}

const COLORS = [
  '#2B3C87',
  '#74AC48',
  '#B7DD9A',
  '#A6AFD9',
  '#6A78B6',
  '#485796',
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#96CEB4',
  '#FFEAA7',
  '#DDA0DD',
  '#98D8C8',
  '#F7DC6F',
  '#BB8FCE',
];

const CustomDelayTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const avgMinutes = data.payload?.avgMinutes;
    return (
      <Paper>
        <Box p={2}>
          <Typography gutterBottom>{data.name}</Typography>
          <Box display="flex" flexDirection="column">
            <Typography variant="h4">{`${data.value} delay${data.value !== 1 ? 's' : ''}`}</Typography>
            {avgMinutes != null && isFinite(avgMinutes) && (
              <Typography variant="body2" color="textSecondary">
                Avg {avgMinutes.toFixed(1)} min
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  }
  return null;
};

const renderCustomizedLegend = props => {
  const { payload } = props;

  return (
    <>
      {payload.map((entry, index) => (
        <span
          key={`legend-${index}`}
          style={{
            color: COLORS[index % COLORS.length],
            marginRight: 10,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              backgroundColor: COLORS[index % COLORS.length],
              borderRadius: '50%',
              marginRight: 5,
            }}
          />
          {entry.value}
        </span>
      ))}
    </>
  );
};

const DelaysBreakdown = (props: DelaysBreakdownProps) => {
  const theme = useTheme();
  const totalCount = props.reasons.reduce((acc, cur) => acc + cur.count, 0);
  const title = String(totalCount);
  const subtitle = totalCount === 1 ? 'Delay' : 'Delays';
  const chartHeight = props.height;

  const [activeIndex, setActiveIndex] = useState<number | undefined>();

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(undefined);
  };

  const data = props.reasons.map((reason, index) => ({
    name: reason.reason,
    value: reason.count,
    avgMinutes: reason.avgMinutes,
    color: COLORS[index % COLORS.length],
  }));

  const containerProps = chartHeight
    ? { width: '100%' as const, height: chartHeight }
    : { width: '100%' as const, height: '100%' as const, aspect: 2 };

  if (!props.reasons || props.reasons.length === 0 || totalCount === 0) {
    return (
      <ResponsiveContainer {...containerProps}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          style={{ color: theme.palette.text.secondary }}
        >
          <Typography variant="h2" style={{ marginBottom: 8 }}>
            0
          </Typography>
          <Typography variant="body2">No Delays</Typography>
        </Box>
      </ResponsiveContainer>
    );
  }

  return (
    <>
      <ResponsiveContainer {...containerProps}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius="50%"
            outerRadius="70%"
            fill="#8884d8"
            stroke="none"
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            paddingAngle={4}
            cornerRadius={2}
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
            activeIndex={activeIndex}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                stroke={activeIndex === index ? '#ffffff' : 'none'}
                strokeWidth={activeIndex === index ? 2 : 0}
              />
            ))}
          </Pie>
          <text
            x="50%"
            y="46%"
            fontSize="1.2vw"
            fontWeight={400}
            fill="white"
            textAnchor="middle"
            dominantBaseline="auto"
          >
            {title}
          </text>
          <text
            x="50%"
            y="50%"
            fontSize=".7vw"
            fontWeight={500}
            fill="rgb(125	163	206)"
            textAnchor="middle"
            dominantBaseline="hanging"
          >
            {subtitle}
          </text>
          <Tooltip
            content={({ active, payload }) => (
              <CustomDelayTooltip active={active} payload={payload} />
            )}
            cursor={{ fill: 'rgba(255,255,255,0.15)' }}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: 4,
              border: 0,
            }}
          />
          <Legend content={renderCustomizedLegend} align="center" verticalAlign="bottom" />
        </PieChart>
      </ResponsiveContainer>
    </>
  );
};

export default DelaysBreakdown;
